import { Heart, ShoppingBag } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import type { Product } from "@/data/products";
import { useWishlist } from "@/hooks/wishlist/useWishlist";
import { useToast } from "@/hooks/use-toast";

const badgeStyles: Record<string, string> = {
  "novo": "bg-primary text-primary-foreground",
  "mais vendido": "bg-accent text-accent-foreground",
  "promoção": "bg-destructive text-destructive-foreground",
};

const ProductCard = ({ product }: { product: Product }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { ids, toggle, requiresLogin } = useWishlist();
  const productDbId = product.product_id ?? (typeof product.id === "string" ? product.id : "");
  const liked = productDbId ? ids.has(productDbId) : false;
  const href = product.slug ? `/produto/${product.slug}` : "#";
  const outOfStock = product.total_stock === 0;

  const handleFavorite = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!productDbId) return;
    if (requiresLogin) {
      toast({
        title: "Entre para favoritar",
        description: "Crie uma conta para salvar seus produtos favoritos.",
      });
      navigate("/login", { state: { from: "/" } });
      return;
    }
    const res = await toggle(productDbId);
    if (res.ok) {
      toast({ title: res.isFav ? "Adicionado aos favoritos" : "Removido dos favoritos" });
    }
  };

  return (
    <div className="group relative overflow-hidden rounded-lg border border-border bg-card shadow-card transition-all duration-300 hover:shadow-card-hover hover:-translate-y-1">
      {/* Image — taller on mobile, square on larger screens */}
      <div className="relative aspect-[3/4] overflow-hidden bg-muted sm:aspect-square">
        <Link to={href} aria-label={product.name} className="block h-full w-full">
          <img
          src={product.image ?? product.image_url ?? "/placeholder.svg"}
          alt={product.name}
          loading="lazy"
          decoding="async"
          width={400}
          height={400}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </Link>
        {/* Badge */}
        {outOfStock ? (
          <span className="absolute left-3 top-3 rounded-full bg-muted px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-muted-foreground border border-border">
            Esgotado
          </span>
        ) : product.badge && (
          <span className={`absolute left-3 top-3 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${badgeStyles[product.badge]}`}>
            {product.badge}
          </span>
        )}
        {/* Favorite */}
        <button
          onClick={handleFavorite}
          className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-card/80 text-foreground backdrop-blur-sm transition-colors hover:bg-card"
          aria-label={liked ? "Remover dos favoritos" : "Adicionar aos favoritos"}
        >
          <Heart className={`h-4 w-4 ${liked ? "fill-accent text-accent" : ""}`} />
        </button>
        {/* Quick add */}
        <Link
          to={href}
          className={`absolute bottom-3 left-1/2 flex -translate-x-1/2 translate-y-12 items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100 ${outOfStock ? "bg-muted text-muted-foreground" : "bg-primary text-primary-foreground hover:bg-primary/90"}`}
        >
          <ShoppingBag className="h-3.5 w-3.5" /> {outOfStock ? "Ver produto" : "Ver opções"}
        </Link>
      </div>
      {/* Info */}
      <Link to={href} className="block p-4">
        <p className="text-xs font-medium text-muted-foreground">{product.category}</p>
        <h3 className="mt-1 text-sm font-semibold leading-snug text-foreground hover:text-primary transition-colors">{product.name}</h3>
        {outOfStock && (
          <p className="mt-1 text-[11px] font-medium text-muted-foreground">Sem estoque</p>
        )}
        <div className="mt-2 flex items-center gap-2">
          <span className="text-lg font-bold text-primary">
            R$ {product.price.toFixed(2).replace(".", ",")}
          </span>
          {(product.originalPrice ?? product.original_price) && (
            <span className="text-xs text-muted-foreground line-through">
              R$ {((product.originalPrice ?? product.original_price)!).toFixed(2).replace(".", ",")}
            </span>
          )}
        </div>
      </Link>
    </div>
  );
};

export default ProductCard;
